import { useSortable } from '@dnd-kit/sortable';
import { wrap } from '@repo/di-ez';
import { MoveCustom, ViewHideLight, EyeLight, PinLightLeft } from '@repo/go-icons';
import { Button, Switch } from '@repo/go-ui';
import type { ReactNode } from 'react';

import styles from './SortableSettingItem.module.scss';

type Props = {
  id: string;
  title?: string;
  isVisible: boolean;
  isVisibilitySwitcherDisabled?: boolean;
  isDragDisabled?: boolean;
  /** When provided, shows unpin control on the right. */
  isPinned?: boolean;
  onVisibilityChange(id: string, value: boolean): void;
  onUnpin?: (id: string) => void;
  extra?: ReactNode;
};

export const SortableSettingItem = wrap<Props>(function SortableSettingItem({
  id,
  title,
  isVisible,
  isPinned = false,
  isDragDisabled = false,
  isVisibilitySwitcherDisabled = false,
  onVisibilityChange,
  onUnpin,
  extra,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: transform ? `translate(0, ${transform.y}px) scaleX(1) scaleY(1)` : undefined,
    transition,
  };

  return (
    <div ref={setNodeRef} className={styles.settingItem} style={style}>
      <div className={styles.leftColumn}>
        <Button
          variant="flat"
          disabled={isDragDisabled || isPinned}
          {...listeners}
          {...attributes}
          className={styles.draggingButton}
        >
          <MoveCustom />
        </Button>

        <Switch
          leftNode={<ViewHideLight />}
          rightNode={<EyeLight />}
          checked={isVisible}
          disabled={isVisibilitySwitcherDisabled}
          onChange={(value) => onVisibilityChange(id, value)}
        />
        <p className="dash-body-regular">{title}</p>
        {extra}
      </div>
      {isPinned && onUnpin && (
        <Button variant="flat" className={styles.unpinButton} onClick={() => onUnpin(id)}>
          <PinLightLeft />
        </Button>
      )}
    </div>
  );
});
